import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import ddt from '@wix/da-ddt';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import FullscreenIcon from '@wix/da-ds/pkg/Icons/24x24/FullView';
import FormatBubbleMenu from '../../../menus/FormatBubbleMenu';
import EverythingMenu from '../../../menus/EverythingMenu/EverythingMenu';
import UniversalMobileMenu from '../../../menus/UniversalMobileMenu';
import PluginInsertMenu from '../../../menus/PluginInsertMenu';
import Viewer from '../../../Viewer/presets/Commission';
import { isTouch } from '../../../utils/browser';
import EditorIconButton from '../../../menus/_partials/EditorIconButton';
import Editor from '../../Editor';
import { EditorProps } from '../../types';
import FullscreenEditor from '../Fullscreen/Fullscreen';
import { extConfig, extConfigFullscreen } from './config';

import s from './Commission.scss';
import presetViewerStyles from '../../../Viewer/presets/Commission/Commission.scss';

const logger = ddt.createLogger('tiptapeditor');

function isTextEmpty(json) {
  if (!json) {
    return true;
  }
  if (json.hasOwnProperty('content') && !json?.content) {
    return true;
  }
  if (json?.html) {
    if (json.html?.features && /"isEmpty":true/.test(json.html?.features)) {
      return true;
    }
  }
  return false;
}

const CommissionEditor: React.FC<
  Omit<EditorProps, 'extensionConfig' | 'presetId'>
> = props => {
  logger.log('CommissionEditor, props', JSON.stringify(props));

  const isMobile = useContext(MobileContext);
  const shouldUseMobileLayout = isMobile || isTouch();
  const { t } = useTranslation('editor');

  if (shouldUseMobileLayout) {
    return (
      <FullscreenEditor
        modalProps={{
          contentClassName: s['fullscreen-bg'],
          heading: t('preset.commission.fullscreen.heading'),
        }}
      >
        {(currentContent, isFullscreen, toggleFs, scrollRef) => {
          return (
            <div
              className={cn(
                s['wrapper'],
                isFullscreen && s['fullscreen'],
                shouldUseMobileLayout && s['mobile']
              )}
            >
              {!isFullscreen && (
                <div className={s['viewer']} onClick={toggleFs}>
                  {isTextEmpty(currentContent?.doc ?? props.initialContent) && (
                    <p className={s['fake-placeholder']}>
                      {t('preset.commission.placeholder')}
                    </p>
                  )}
                  <Viewer
                    className={s['viewer-inner']}
                    contentJson={currentContent?.doc ?? props.initialContent}
                    {...props}
                  />
                </div>
              )}

              {isFullscreen && (
                <div className={cn(s['stuck'])}>
                  <UniversalMobileMenu />
                </div>
              )}

              {isFullscreen && (
                <div className={cn(s['fixed-wrapper'])}>
                  <Editor
                    {...props}
                    withinModal
                    presetId="commission"
                    className={s['root']}
                    extensionConfig={
                      isFullscreen ? extConfigFullscreen : extConfig
                    }
                    initialContent={currentContent?.doc ?? props.initialContent}
                    initialSelection={currentContent?.selection}
                    scrollContainerRef={scrollRef}
                    autoScrollWhenWithin={50}
                    placeholder={t('preset.commission.placeholder')}
                  >
                    {(_, scrollContainer) => (
                      <>
                        {!isFullscreen && (
                          <FormatBubbleMenu
                            scrollContainerRef={scrollContainer}
                          />
                        )}
                      </>
                    )}
                  </Editor>
                </div>
              )}
            </div>
          );
        }}
      </FullscreenEditor>
    );
  }

  return (
    <FullscreenEditor
      modalProps={{
        contentClassName: s['fullscreen-bg'],
        heading: t('preset.commission.fullscreen.heading'),
      }}
    >
      {(currentContent, isFullscreen, toggleFs, scrollRef) => {
        return (
          <>
            {isFullscreen && (
              <div className={cn(s['stuck'])}>
                <EverythingMenu wrapping={false} />
              </div>
            )}
            <div className={cn(isFullscreen && s['fixed-wrapper'])}>
              <Editor
                {...props}
                withinModal
                presetId="commission"
                className={cn(
                  s['editor'],
                  presetViewerStyles['root'],
                  !isFullscreen && s['in-form']
                )}
                extensionConfig={isFullscreen ? extConfigFullscreen : extConfig}
                initialContent={currentContent?.doc ?? props.initialContent}
                initialSelection={
                  isFullscreen && !isTouch() && currentContent?.selection
                }
                selfScrollContainer={!isFullscreen}
                scrollContainerRef={isFullscreen ? scrollRef : undefined}
                autoScrollWhenWithin={50}
                placeholder={t('preset.commission.placeholder')}
              >
                {(_, scrollContainer) => (
                  <>
                    {!isFullscreen && (
                      <FormatBubbleMenu scrollContainerRef={scrollContainer} />
                    )}
                  </>
                )}
              </Editor>
            </div>

            {!isFullscreen && (
              <div className={cn(s['toolbar'])}>
                <PluginInsertMenu />
                <EditorIconButton
                  featureId="action:fullscreen"
                  icon={FullscreenIcon}
                  onClick={toggleFs}
                  aria-label={t('button.fullscreen')}
                  tooltipText={t('button.fullscreen')}
                />
              </div>
            )}
          </>
        );
      }}
    </FullscreenEditor>
  );
};
CommissionEditor.displayName = 'CommissionEditor';

export default CommissionEditor;
